import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { TrendingDown, TrendingUp } from "lucide-react";
import { Helmet } from "react-helmet";
import ContainerPage from "../../../components/Containers/ContainerPage";
import TitlePage from "../../../components/Texts/TitlePage";
import DescriptionPage from "../../../components/Texts/DescriptionPage";
import Loader from "../../../components/Loaders/Loader";
import { useTranslationApp } from "../../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../../lib/redux";
import { getRoleCapability } from "../../../actions/getters";
import {
  BRAND_INSIGHTS_PAGE_TITLE,
  BRAND_INSIGHTS_PAGE_DESCRIPTION,
  BRAND_INSIGHTS_POSITIVE_TITLE,
  BRAND_INSIGHTS_NEGATIVE_TITLE,
  BRAND_INSIGHTS_POSITIVE_SUBTITLE,
  BRAND_INSIGHTS_NEGATIVE_SUBTITLE,
  BRAND_INSIGHTS_EMPTY,
  BRAND_INSIGHTS_COUNT,
} from "../../../i18n/keysTranslations";
import { getInsightCopy } from "../../../actions/brandLearnedInsights";
import { isInsightNewSince } from "../../../utils/brandInsightsUnread";
import { useBrandLearnedInsights } from "./hooks/useBrandLearnedInsights";
import BrandInsightCard from "./components/BrandInsightCard";
import InsightSection from "./components/InsightSection";
import ApplyInsightModal from "./components/ApplyInsightModal";

const BrandInsights = () => {
  const { t, i18n } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { theme } = useSelectorApp((state) => state.ui);
  const canEditBrandSetup = dispatch(getRoleCapability("canEditBrandSetup"));
  const {
    playbook,
    lastViewedAtOnEnter,
    isLoading,
    hasInsights,
    applyInsight,
    proposal,
    isProposing,
    isConfirming,
    applyError,
    dismissingKey,
    openApply,
    closeApply,
    confirmApply,
    retryApply,
    dismiss,
  } = useBrandLearnedInsights();

  const canApply = Boolean(canEditBrandSetup && hasInsights);
  const positiveInsights = playbook.positive || [];
  const negativeInsights = playbook.negative || [];

  const renderInsightCards = ({ insights, polarity }) =>
    insights.map((insight, index) => {
      const copy = getInsightCopy(insight, i18n.language);
      return (
        <BrandInsightCard
          key={insight.topicKey}
          title={copy.title}
          description={copy.description}
          topicKey={insight.topicKey}
          polarity={polarity}
          canApply={canApply}
          isDismissing={dismissingKey === `${polarity}:${index}`}
          isNew={isInsightNewSince(insight, lastViewedAtOnEnter)}
          onReview={() => openApply({ ...insight, ...copy, polarity, index })}
          onDismiss={() => dismiss({ polarity, index })}
        />
      );
    });

  return (
    <>
      <Helmet>
        <title>{`${theme.nameBusiness} - ${t(BRAND_INSIGHTS_PAGE_TITLE)}`}</title>
      </Helmet>
      <ContainerPage hasMarginBottom={false} sx={{ width: "100%", px: 4.5 }}>
        <TitlePage
          sx={{
            mt: 5,
            mb: 1,
          }}
        >
          {t(BRAND_INSIGHTS_PAGE_TITLE)}
        </TitlePage>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
            flex: 1,
            minHeight: 0,
            pb: hasInsights ? 15 : 0,
          }}
        >
          <DescriptionPage>
            {t(BRAND_INSIGHTS_PAGE_DESCRIPTION)}
          </DescriptionPage>
          {isLoading ? (
            <Loader />
          ) : !hasInsights ? (
            <Box
              sx={{
                display: "flex",
                flex: 1,
                width: "100%",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "calc(100vh - 380px)",
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
              >
                {t(BRAND_INSIGHTS_EMPTY)}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {positiveInsights.length > 0 ? (
                <InsightSection
                  title={t(BRAND_INSIGHTS_POSITIVE_TITLE)}
                  subtitle={t(BRAND_INSIGHTS_POSITIVE_SUBTITLE)}
                  countLabel={t(BRAND_INSIGHTS_COUNT, {
                    count: positiveInsights.length,
                  })}
                  tone="positive"
                  icon={TrendingUp}
                >
                  {renderInsightCards({
                    insights: positiveInsights,
                    polarity: "positive",
                  })}
                </InsightSection>
              ) : null}
              {negativeInsights.length > 0 ? (
                <InsightSection
                  title={t(BRAND_INSIGHTS_NEGATIVE_TITLE)}
                  subtitle={t(BRAND_INSIGHTS_NEGATIVE_SUBTITLE)}
                  countLabel={t(BRAND_INSIGHTS_COUNT, {
                    count: negativeInsights.length,
                  })}
                  tone="negative"
                  icon={TrendingDown}
                >
                  {renderInsightCards({
                    insights: negativeInsights,
                    polarity: "negative",
                  })}
                </InsightSection>
              ) : null}
            </Box>
          )}
          <ApplyInsightModal
            modalOpen={Boolean(applyInsight)}
            isProposing={isProposing}
            isConfirming={isConfirming}
            proposal={proposal}
            applyError={applyError}
            onCloseModal={closeApply}
            onConfirm={confirmApply}
            onRetry={retryApply}
          />
        </Box>
      </ContainerPage>
    </>
  );
};

export default BrandInsights;
